/**
 * DangoteMonthlyReport — Dynamic Input + Calculation Engine
 * Route: /corporate/reporting
 *
 * This page IS the source of truth for all monthly data.
 * All downstream pages read from this monthly calculation output.
 *
 * Flow: Period → BU → Asset → Enter Fields → Calculate → Output → Submit
 */
import React, { useState, useCallback, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDangote } from "@/context/DangoteContext";
import { PageHeader, SectionCard } from "@/components/ds";
import {
  RefreshCw, Upload, Download, CheckCircle2, Clock, Lock,
  AlertTriangle, ChevronRight, X, Activity, Recycle, Calculator, Info,
} from "lucide-react";
import client from "@/api/client";

// ── Field Schemas per BU type ─────────────────────────────────────────────────
const FIELD_SCHEMA = {
  cement: {
    groups: [
      {
        label: "Fossil Fuels",
        color: "border-red-200 bg-red-50/40",
        icon: "🔥",
        fields: [
          { key: "coal_t",     label: "Coal consumed",   unit: "tonnes", required: true,  hint: "Bituminous coal for kiln firing",  placeholder: "e.g. 58500" },
          { key: "petcoke_t",  label: "Petroleum coke",  unit: "tonnes", required: false, hint: "Petcoke co-fired with coal",        placeholder: "e.g. 6800" },
          { key: "diesel_t",   label: "Diesel consumed",  unit: "tonnes", required: true,  hint: "Plant vehicles + generators",       placeholder: "e.g. 1200" },
        ],
      },
      {
        label: "AFR & Alternative Fuels",
        color: "border-green-200 bg-green-50/40",
        icon: "♻️",
        fields: [
          { key: "afr_t",             label: "Total AFR input",             unit: "tonnes", required: false, hint: "Total alternative fuels displacing coal", placeholder: "e.g. 4100" },
          { key: "afr_rdf_t",         label: "↳ RDF (Refuse Derived Fuel)", unit: "tonnes", required: false, hint: "Municipal/industrial RDF component",   placeholder: "e.g. 1500" },
          { key: "afr_biomass_t",     label: "↳ Biomass/agro waste",        unit: "tonnes", required: false, hint: "Agricultural residues, crop waste",    placeholder: "e.g. 800" },
          { key: "afr_tyres_t",       label: "↳ Tyre chips/shreds",         unit: "tonnes", required: false, hint: "Waste tyres as fuel",                 placeholder: "e.g. 500" },
          { key: "afr_avg_lhv_gj_t", label: "AFR average LHV",             unit: "GJ/t",   required: false, hint: "Lower heating value (default 15 GJ/t if unknown)", placeholder: "e.g. 15.0" },
          { key: "afr_biogenic_pct",  label: "AFR biogenic fraction",       unit: "%",      required: false, hint: "% biogenic energy (biomass/organic) — affects fossil CO₂ split", placeholder: "e.g. 40" },
          { key: "afr_moisture_pct",  label: "AFR moisture content",        unit: "%",      required: false, hint: "Moisture reduces effective LHV", placeholder: "e.g. 20" },
        ],
      },
      {
        label: "Production Volumes",
        color: "border-orange-200 bg-orange-50/40",
        icon: "🏭",
        fields: [
          { key: "clinker_production_t", label: "Clinker production",  unit: "tonnes", required: true,  hint: "Critical — drives process emissions (507 kgCO₂/t)", placeholder: "e.g. 185000" },
          { key: "cement_production_t",  label: "Cement production",   unit: "tonnes", required: true,  hint: "Total cement dispatched",                          placeholder: "e.g. 238000" },
        ],
      },
      {
        label: "Energy",
        color: "border-blue-200 bg-blue-50/40",
        icon: "⚡",
        fields: [
          { key: "electricity_kwh", label: "Electricity consumed", unit: "kWh", required: true, hint: "Total plant electricity (Scope 2)", placeholder: "e.g. 82000000" },
        ],
      },
      {
        label: "Circularity Inputs",
        color: "border-teal-200 bg-teal-50/40",
        icon: "♻️",
        fields: [
          { key: "alt_raw_materials_t",   label: "Alternative raw materials",  unit: "tonnes",  required: false, hint: "Fly ash, slag, pozzolans replacing limestone",   placeholder: "e.g. 28000" },
          { key: "total_raw_materials_t", label: "Total raw materials",        unit: "tonnes",  required: false, hint: "All raw materials consumed (normaliser)",          placeholder: "e.g. 520000" },
          { key: "fly_ash_t",             label: "Fly ash used as SCM",        unit: "tonnes",  required: false, hint: "Supplementary cementitious material",              placeholder: "e.g. 12000" },
        ],
      },
      {
        label: "Scope 3 (Optional)",
        color: "border-purple-200 bg-purple-50/40",
        icon: "🚚",
        fields: [
          { key: "scope3_transport_tkm", label: "Supply chain transport", unit: "tonne-km", required: false, hint: "Clinker/cement distribution tonne-kilometres", placeholder: "e.g. 8500000" },
        ],
      },
    ],
  },
  refinery: {
    groups: [
      {
        label: "Crude & Production",
        color: "border-orange-200 bg-orange-50/40",
        icon: "🛢️",
        fields: [
          { key: "crude_processed_bbl", label: "Crude oil processed", unit: "barrels", required: true,  hint: "Total crude throughput this period", placeholder: "e.g. 8680000" },
        ],
      },
      {
        label: "Fuel Consumption",
        color: "border-red-200 bg-red-50/40",
        icon: "🔥",
        fields: [
          { key: "fuel_gas_t",  label: "Fuel gas consumed",  unit: "tonnes",  required: true,  hint: "Refinery fuel gas (SMR off-gas etc.)", placeholder: "e.g. 85000" },
          { key: "diesel_t",    label: "Diesel consumed",    unit: "tonnes",  required: false, hint: "Diesel for auxiliary equipment",       placeholder: "e.g. 3200" },
          { key: "flared_gas_kNm3", label: "Flare gas volume", unit: "kNm³", required: true,  hint: "Routine + non-routine flaring",        placeholder: "e.g. 12000" },
        ],
      },
      {
        label: "Energy",
        color: "border-blue-200 bg-blue-50/40",
        icon: "⚡",
        fields: [
          { key: "electricity_kwh", label: "Electricity consumed", unit: "kWh", required: true, hint: "Purchased grid electricity", placeholder: "e.g. 185000000" },
        ],
      },
    ],
  },
  fertilizer: {
    groups: [
      {
        label: "Production Volumes",
        color: "border-yellow-200 bg-yellow-50/40",
        icon: "🌿",
        fields: [
          { key: "urea_production_t", label: "Urea production", unit: "tonnes", required: true, hint: "Urea produced (CO₂ captured in process)", placeholder: "e.g. 248000" },
        ],
      },
      {
        label: "Fuel & Feedstock",
        color: "border-red-200 bg-red-50/40",
        icon: "🔥",
        fields: [
          { key: "natural_gas_t", label: "Natural gas consumed", unit: "tonnes", required: true, hint: "NG for SMR (feedstock + fuel)", placeholder: "e.g. 98000" },
        ],
      },
      {
        label: "Energy",
        color: "border-blue-200 bg-blue-50/40",
        icon: "⚡",
        fields: [
          { key: "electricity_kwh", label: "Electricity consumed", unit: "kWh", required: true, hint: "Plant electricity", placeholder: "e.g. 125000000" },
        ],
      },
    ],
  },
  sugar: {
    groups: [
      {
        label: "Production Volumes",
        color: "border-green-200 bg-green-50/40",
        icon: "🍬",
        fields: [
          { key: "sugarcane_t",      label: "Sugarcane processed", unit: "tonnes", required: true,  hint: "Total cane through the mill",             placeholder: "e.g. 850000" },
          { key: "sugar_production_t",label:"Sugar production",    unit: "tonnes", required: true,  hint: "Refined sugar output",                    placeholder: "e.g. 95000" },
        ],
      },
      {
        label: "Fuel & Energy",
        color: "border-red-200 bg-red-50/40",
        icon: "🔥",
        fields: [
          { key: "diesel_t",        label: "Diesel consumed",     unit: "tonnes", required: false, hint: "Auxiliary diesel — bagasse is biogenic ≠ 0 net", placeholder: "e.g. 800" },
          { key: "electricity_kwh", label: "Grid electricity",    unit: "kWh",    required: true,  hint: "Purchased electricity (Scope 2)",           placeholder: "e.g. 42000000" },
        ],
      },
    ],
  },
  logistics: {
    groups: [
      {
        label: "Fleet Operations",
        color: "border-blue-200 bg-blue-50/40",
        icon: "🚛",
        fields: [
          { key: "trucks",     label: "Truck count",         unit: "units", required: true,  hint: "Total operational heavy trucks",        placeholder: "e.g. 12000" },
          { key: "fleet_km",   label: "Total fleet distance", unit: "km",   required: true,  hint: "Sum of all truck kilometres this month", placeholder: "e.g. 96000000" },
          { key: "diesel_t",   label: "Diesel consumed",      unit: "tonnes",required: true,  hint: "Total fleet diesel consumption",        placeholder: "e.g. 18500" },
        ],
      },
      {
        label: "Energy & Scope 3",
        color: "border-purple-200 bg-purple-50/40",
        icon: "⚡",
        fields: [
          { key: "electricity_kwh",     label: "Depot electricity",    unit: "kWh",      required: false, hint: "Depot / warehouse electricity use",    placeholder: "e.g. 8000000" },
          { key: "scope3_transport_tkm",label: "Sub-contracted haulage",unit: "tonne-km", required: false, hint: "Third-party contractor tonne-km",       placeholder: "e.g. 0" },
        ],
      },
    ],
  },
};

const WORKFLOW = ["draft", "submitted", "under_review", "approved", "locked"];
const STATUS_CONFIG = {
  draft:        { label: "Draft",        color: "bg-slate-100 text-slate-600 border-slate-200", dot: "bg-slate-400" },
  submitted:    { label: "Submitted",    color: "bg-blue-100 text-blue-700 border-blue-200",    dot: "bg-blue-500" },
  under_review: { label: "Under Review", color: "bg-amber-100 text-amber-700 border-amber-200", dot: "bg-amber-500" },
  approved:     { label: "Approved",     color: "bg-teal-100 text-teal-700 border-teal-200",    dot: "bg-teal-500" },
  locked:       { label: "Locked",       color: "bg-slate-100 text-slate-500 border-slate-200", dot: "bg-slate-400" },
};

// ── Upload Zone ───────────────────────────────────────────────────────────────
function UploadZone({ onUpload, uploading }) {
  const [drag, setDrag] = useState(false);
  const inputRef = useRef(null);
  const handle = useCallback((file) => {
    if (!file) return;
    const ext = file.name.toLowerCase().slice(file.name.lastIndexOf("."));
    if (![".xlsx",".xls",".csv"].includes(ext)) { alert("Please upload .xlsx, .xls, or .csv"); return; }
    onUpload(file);
  }, [onUpload]);
  return (
    <div onDragOver={e=>{e.preventDefault();setDrag(true)}} onDragLeave={()=>setDrag(false)}
      onDrop={e=>{e.preventDefault();setDrag(false);handle(e.dataTransfer.files[0])}}
      className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all ${drag?"border-teal-400 bg-teal-50":"border-slate-300 hover:border-teal-400 hover:bg-teal-50/30"}`}
      onClick={()=>!uploading&&inputRef.current?.click()} data-testid="upload-zone">
      <input ref={inputRef} type="file" accept=".xlsx,.xls,.csv" className="hidden"
        onChange={e=>handle(e.target.files?.[0])} />
      {uploading ? (
        <div className="flex flex-col items-center gap-2">
          <RefreshCw size={22} className="text-teal-500 animate-spin" />
          <p className="text-sm font-semibold text-teal-700">Uploading and calculating...</p>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-2">
          <Upload size={22} className="text-slate-400" />
          <p className="text-sm font-semibold text-slate-700">Drop Excel or CSV here</p>
          <p className="text-xs text-slate-400">.xlsx / .xls / .csv</p>
          <button className="mt-1 px-3 py-1.5 bg-teal-600 text-white text-xs font-bold rounded-lg">Browse Files</button>
        </div>
      )}
    </div>
  );
}

// ── Calculation Output Block ──────────────────────────────────────────────────
function CalcOutput({ result, assetType }) {
  const [showFactors, setShowFactors] = React.useState(false);
  if (!result) return null;

  const s1 = result.scope1?.total_tco2e || 0;
  const s2 = result.scope2?.electricity_tco2e || 0;
  const s3 = result.scope3?.total_tco2e || 0;
  const total = result.total_tco2e || 0;
  const circ  = result.circularity || {};
  const baseline = result.baseline || {};
  const circVal  = result.circularity_valuation || {};
  const transparency = result.transparency || {};
  const s3Struct = result.scope3?.structured || [];
  const maxBar = Math.max(s1, s2, s3, 1);

  // Baseline/project/reduction display
  const bTco2e  = baseline.baseline_tco2e || 0;
  const pTco2e  = baseline.project_tco2e  || total;
  const rTco2e  = baseline.reduction_tco2e || 0;
  const rPct    = baseline.reduction_pct || 0;
  const isAboveBenchmark = rTco2e < 0;
  const maxBPBar = Math.max(bTco2e, pTco2e, 1);

  return (
    <div className="space-y-5" data-testid="calc-output">
      {/* ── A. Baseline / Project / Reduction ── same as Oko-Oba ──────────── */}
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Activity size={14} className="text-teal-600" />
            <p className="text-xs font-bold text-slate-700 uppercase tracking-wide">Baseline vs Project vs Reduction</p>
          </div>
          <span className={`text-xs font-bold px-2 py-0.5 rounded-full border ${
            isAboveBenchmark ? "bg-red-100 text-red-700 border-red-200" : "bg-emerald-100 text-emerald-700 border-emerald-200"
          }`}>
            {isAboveBenchmark
              ? `${Math.abs(rPct)}% above benchmark`
              : `${rPct}% reduction`}
          </span>
        </div>

        {[
          { label: "Baseline (reference)", value: bTco2e, color: "bg-red-400" },
          { label: "Project (current actuals)", value: pTco2e, color: "bg-teal-500" },
          ...(rTco2e !== 0 ? [{
            label: isAboveBenchmark ? "Gap to benchmark" : "Reduction achieved",
            value: Math.abs(rTco2e),
            color: isAboveBenchmark ? "bg-orange-400" : "bg-emerald-500",
          }] : []),
        ].map(({ label, value, color }) => {
          const pct = maxBPBar > 0 ? Math.min((value/maxBPBar)*100, 100) : 0;
          return (
            <div key={label} className="mb-2.5">
              <div className="flex justify-between text-xs mb-1">
                <span className="text-slate-600 font-semibold">{label}</span>
                <span className="font-bold text-slate-900 tabular-nums">{Number(value).toLocaleString()} tCO₂e</span>
              </div>
              <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                <div className={`h-full rounded-full ${color} transition-all`} style={{ width: `${pct}%` }} />
              </div>
            </div>
          );
        })}

        <div className="mt-3 pt-2 border-t border-slate-100 text-xs text-slate-400 italic">
          {baseline.source_label}
          {isAboveBenchmark && (
            <p className="text-amber-600 mt-0.5 not-italic font-semibold">
              Reduction opportunities identified — see circularity valuation and carbon strategy below
            </p>
          )}
        </div>
      </div>

      {/* ── B. Scope 1 / 2 / 3 Structure ─────────────────────────────────── */}
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <p className="text-xs font-bold text-slate-700 uppercase tracking-wide">Scope 1 / 2 / 3 Structure</p>
          <span className="text-sm font-bold text-slate-900">{Number(total).toLocaleString()} tCO₂e/month</span>
        </div>

        {[
          { label: "Scope 1 — Combustion + Process", value: s1, color: "bg-red-400" },
          { label: "Scope 2 — Electricity",           value: s2, color: "bg-orange-400" },
          ...(s3 > 0 ? [{ label: "Scope 3 — Transport", value: s3, color: "bg-purple-400" }] : []),
        ].map(({ label, value, color }) => {
          const pct = maxBar > 0 ? Math.min((value/maxBar)*100, 100) : 0;
          return (
            <div key={label} className="mb-2.5">
              <div className="flex justify-between text-xs mb-1">
                <span className="text-slate-600">{label}</span>
                <span className="font-bold text-slate-900">{Number(value).toLocaleString()} tCO₂e</span>
              </div>
              <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                <div className={`h-full rounded-full ${color}`} style={{ width: `${pct}%` }} />
              </div>
            </div>
          );
        })}

        {/* Scope 1 breakdown if cement */}
        {assetType === "cement" && result.scope1?.combustion_tco2e > 0 && (
          <div className="mt-3 pt-3 border-t border-slate-100 grid grid-cols-3 gap-2 text-xs">
            {[
              ["Combustion", result.scope1?.combustion_tco2e],
              ["Process",    result.scope1?.process_tco2e],
              ["AFR credit", result.scope1?.breakdown?.afr_credit],
            ].filter(([,v]) => v).map(([l,v]) => (
              <div key={l} className="bg-slate-50 rounded-lg px-2.5 py-2 border border-slate-100">
                <p className="text-slate-400">{l}</p>
                <p className="font-bold text-slate-900">{Number(v).toLocaleString()}</p>
              </div>
            ))}
          </div>
        )}

        {/* Structured Scope 3 */}
        {s3Struct.length > 0 && (
          <div className="mt-3 pt-3 border-t border-slate-100">
            <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">Scope 3 — GHG Protocol Categories</p>
            {s3Struct.map(cat => (
              <div key={cat.category} className="flex items-center justify-between text-xs py-1 border-b border-slate-50 last:border-0">
                <span className={`${cat.status === "Calculated" ? "text-slate-600" : "text-slate-400 italic"}`}>
                  {cat.category}
                </span>
                <span className={`font-semibold ${
                  cat.status === "Calculated" ? "text-purple-700" : "text-slate-300"
                }`}>
                  {cat.tco2e != null ? `${Number(cat.tco2e).toLocaleString()} tCO₂e` : cat.status}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── C. Circularity ──────────────────────────────────────────────── */}
      {assetType === "cement" && Object.keys(circ).length > 0 && (
        <div className={`border rounded-xl p-4 ${circ.afr_substitution_pct > 5 ? "bg-teal-50 border-teal-200" : "bg-amber-50 border-amber-200"}`}>
          <div className="flex items-center gap-2 mb-3">
            <Recycle size={13} />
            <p className="text-xs font-bold uppercase tracking-wide">Circularity Metrics</p>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
            {[
              {l:"AFR Rate",         v:`${circ.afr_substitution_pct?.toFixed(1)}%`,      ok:circ.afr_substitution_pct>5},
              {l:"Clinker/Cement",   v:`${circ.clinker_to_cement_ratio?.toFixed(3)}`,    ok:circ.clinker_to_cement_ratio<0.80},
              {l:"Alt Raw Materials",v:`${circ.alt_raw_materials_pct?.toFixed(1)}%`,     ok:circ.alt_raw_materials_pct>3},
              {l:"Fly Ash SCM",      v:`${circ.fly_ash_use_pct?.toFixed(1)}%`,           ok:circ.fly_ash_use_pct>2},
            ].map(({l,v,ok}) => (
              <div key={l} className="bg-white/70 rounded-lg px-3 py-2">
                <div className="flex items-center gap-1 mb-0.5"><div className={`w-1.5 h-1.5 rounded-full ${ok?"bg-teal-500":"bg-amber-400"}`}/></div>
                <p className="text-xs text-slate-400">{l}</p>
                <p className={`text-sm font-bold ${ok?"text-teal-800":"text-amber-800"}`}>{v}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── D. Circularity Valuation ($) ──────────────────────────────── */}
      {(circVal.items || []).length > 0 && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5">
          {/* SCM clarity outputs — near clinker/SCM inputs */}
          {assetType === "cement" && circ.clinker_to_cement_ratio > 0 && (
            <div className="mb-4 pb-3 border-b border-emerald-200">
              <p className="text-[9px] font-bold text-emerald-700 uppercase tracking-wide mb-2">SCM Clarity Outputs</p>
              <div className="grid grid-cols-2 gap-2">
                {(() => {
                  const clinkerT = circ.clinker_production_t || 0;
                  const actualRatio = circ.clinker_to_cement_ratio || 0;
                  const targetRatio = 0.70;
                  const clinkerAvoided = actualRatio > targetRatio
                    ? Math.round(clinkerT * (actualRatio - targetRatio))
                    : 0;
                  const processAvoided = Math.round(clinkerAvoided * 0.507);
                  return [
                    { l: "Clinker avoided (if ratio → 0.70)", v: `${clinkerAvoided.toLocaleString()} t` },
                    { l: "Process emissions avoided", v: `${processAvoided.toLocaleString()} tCO₂` },
                  ].map(({ l, v }) => (
                    <div key={l} className="bg-white/60 rounded-lg px-2.5 py-2">
                      <p className="text-[9px] text-emerald-600">{l}</p>
                      <p className="text-sm font-bold text-emerald-800">{v}</p>
                    </div>
                  ));
                })()}
              </div>
            </div>
          )}
            <div className="flex items-center gap-2">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-bold text-emerald-800 uppercase tracking-wide">Circularity Value Created</p>
            </div>
            <span className="text-sm font-bold text-emerald-700">
              ${Number(circVal.total_value_usd_yr/1e6).toFixed(1)}M/yr total
            </span>
          </div>
          {circVal.items.map(item => (
            <div key={item.category} className="flex items-start justify-between py-2 border-b border-emerald-100 last:border-0 text-xs">
              <div className="flex-1 min-w-0 mr-2">
                <p className="font-semibold text-emerald-800">{item.category}</p>
                <p className="text-emerald-600 italic">{item.description}</p>
                <p className="text-slate-400 mt-0.5">{item.unit}</p>
              </div>
              <div className="text-right flex-shrink-0">
                <p className="font-bold text-emerald-700">${Number(item.total_usd_yr/1e6).toFixed(2)}M/yr</p>
                <p className="text-emerald-500">${Number(item.total_usd_month||0).toLocaleString()}/month</p>
              </div>
            </div>
          ))}
          <p className="text-[9px] text-slate-400 italic mt-2">
            Assumptions: Coal $150/t · AFR gate fee -$20/t · Carbon $25/tCO₂e · Clinker $90/t. Indicative only.
          </p>
        </div>
      )}

      {/* ── E. Calculation Transparency ────────────────────────────────── */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <button className="w-full flex items-center justify-between px-5 py-3 bg-slate-50 hover:bg-slate-100 text-left"
          onClick={() => setShowFactors(v => !v)}>
          <div className="flex items-center gap-2">
            <p className="text-xs font-bold text-slate-600 uppercase tracking-wide">Calculation Transparency</p>
            {transparency.calculation_id && (
              <span className="text-[9px] font-mono text-slate-400">ID: {transparency.calculation_id}</span>
            )}
          </div>
          <span className="text-xs text-slate-400">{showFactors ? "▲" : "▼"} {(transparency.factor_table||[]).length} factors</span>
        </button>
        {showFactors && (
          <div className="px-5 pb-4 pt-3">
            <div className="grid grid-cols-2 gap-2 text-xs mb-3">
              {[
                ["Protocol",          transparency.protocol],
                ["Calculation Basis", transparency.calculation_basis?.split("+")[0]?.trim()],
                ["GWP Standard",      transparency.gwp_standard?.split("—")[0]?.trim()],
                ["Scope 2 Method",    transparency.scope2_approach?.split("(")[0]?.trim()],
              ].filter(([,v]) => v).map(([l,v]) => (
                <div key={l}>
                  <p className="text-slate-400">{l}</p>
                  <p className="font-semibold text-slate-700">{v}</p>
                </div>
              ))}
            </div>
            {(transparency.factor_table||[]).length > 0 && (
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-slate-100">
                    {["Factor","Value","Unit","Source","Type","Confidence"].map(h => (
                      <th key={h} className="text-left text-slate-400 pb-1.5 pr-3 font-semibold text-[9px] uppercase">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(transparency.factor_table||[]).map((f,i) => (
                    <tr key={i} className="border-b border-slate-50 last:border-0">
                      <td className="py-1.5 pr-3 font-semibold text-slate-700">{f.factor}</td>
                      <td className="py-1.5 pr-3 text-slate-900 font-bold tabular-nums">{f.value}</td>
                      <td className="py-1.5 pr-3 text-slate-400">{f.unit}</td>
                      <td className="py-1.5 pr-3 text-slate-400 max-w-[120px] truncate">{f.source?.split(" (")[0]}</td>
                      <td className="py-1.5 pr-3">
                        <span className={`font-semibold ${
                          f.type === "Default" ? "text-amber-600" :
                          f.type === "Country" ? "text-teal-600" :
                          f.type === "Methodology" ? "text-purple-600" : "text-slate-500"
                        }`}>{f.type}</span>
                      </td>
                      <td className="py-1.5 text-slate-400">{typeof f.confidence === "string" ? f.confidence.split(".")[0] : f.confidence}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>

      <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl px-4 py-2.5">
        <Info size={11} className="text-amber-500 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-amber-700 italic">
          Validation-Ready Model Output · Not independently verified · IPCC 2006 + DCI-CORPORATE-V1.0 ·
          Hash: {(result.calc_hash||"").slice(0,16)}...
        </p>
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function DangoteMonthlyReport() {
  const navigate = useNavigate();
  const {
    periods, selectedPeriod, setSelectedPeriod,
    period, loading, refresh,
    importing, importResult, uploadFile, setImportResult,
    transitionPeriod,
  } = useDangote();

  const [allBUs,       setAllBUs]       = useState([]);
  const [buAssets,     setBuAssets]     = useState([]);
  const [selectedBU,   setSelectedBU]   = useState(null);
  const [selectedAsset,setSelectedAsset]= useState(null);
  const [assetType,    setAssetType]    = useState("cement");
  const [form,         setForm]         = useState({});
  const [savedForm,    setSavedForm]    = useState({});
  const [calcResult,   setCalcResult]   = useState(null);
  const [calculating,  setCalculating]  = useState(false);
  const [saving,       setSaving]       = useState(false);
  const [saveMsg,      setSaveMsg]      = useState(null);
  const [transitioning,setTransitioning]= useState(false);

  // Load BUs
  useEffect(() => {
    client.get("/dangote/business-units").then(r => {
      setAllBUs(r.data || []);
      if (!selectedBU && r.data?.length > 0) {
        setSelectedBU(r.data[0]);
      }
    }).catch(() => {});
  }, []);

  // Load assets when BU changes
  useEffect(() => {
    if (!selectedBU) return;
    client.get(`/dangote/business-units/${selectedBU.bu_id}?period_id=${selectedPeriod}`)
      .then(r => {
        const assets = r.data?.assets || [];
        setBuAssets(assets);
        if (!selectedAsset || !assets.find(a => a.asset_id === selectedAsset?.asset_id)) {
          setSelectedAsset(assets[0] || null);
        }
      }).catch(() => {});
  }, [selectedBU, selectedPeriod]);

  // Set asset type and load saved data when asset changes
  useEffect(() => {
    if (!selectedAsset || !selectedPeriod) return;
    const type = selectedBU?.sector_type || "cement";
    setAssetType(type);
    setCalcResult(null);

    // Load saved submission for this asset/period
    client.get(`/dangote/submissions/${selectedAsset.asset_id}/${selectedPeriod}`)
      .then(r => {
        const ad = r.data?.activity_data || {};
        setForm(ad);
        setSavedForm(ad);
      })
      .catch(() => { setForm({}); setSavedForm({}); });

    // Load existing calculation if any
    client.get(`/dangote/calculations/${selectedAsset.asset_id}/${selectedPeriod}`)
      .then(r => setCalcResult(r.data?.results || null))
      .catch(() => {});
  }, [selectedAsset, selectedPeriod, selectedBU]);

  const handleFieldChange = useCallback((key, val) => {
    setForm(prev => ({ ...prev, [key]: val === "" ? "" : Number(val) || val }));
  }, []);

  const handleSaveDraft = useCallback(async () => {
    if (!selectedAsset) return;
    setSaving(true);
    try {
      await client.post("/dangote/submissions", {
        asset_id:      selectedAsset.asset_id,
        bu_id:         selectedBU?.bu_id,
        period_id:     selectedPeriod,
        status:        "draft",
        activity_data: form,
      });
      setSavedForm(form);
      setSaveMsg({ type: "ok", text: "Draft saved" });
      setTimeout(() => setSaveMsg(null), 3000);
    } catch(e) {
      setSaveMsg({ type: "err", text: "Save failed" });
    }
    setSaving(false);
  }, [selectedAsset, selectedBU, selectedPeriod, form]);

  const handleCalculate = useCallback(async () => {
    if (!selectedAsset) return;
    setCalculating(true);
    try {
      // Save first
      await client.post("/dangote/submissions", {
        asset_id:      selectedAsset.asset_id,
        bu_id:         selectedBU?.bu_id,
        period_id:     selectedPeriod,
        status:        "submitted",
        activity_data: form,
      });
      // Then calculate
      const r = await client.post(`/dangote/calculate/${selectedAsset.asset_id}/${selectedPeriod}`);
      if (r.data?.total_tco2e !== undefined) {
        setCalcResult({ total_tco2e: r.data.total_tco2e });
      }
      // Load full calculation result
      const detail = await client.get(`/dangote/calculations/${selectedAsset.asset_id}/${selectedPeriod}`);
      setCalcResult(detail.data?.results || null);
      setSavedForm(form);
      refresh();
      setSaveMsg({ type: "ok", text: `Calculated: ${Number(r.data?.total_tco2e||0).toLocaleString()} tCO₂e/month` });
      setTimeout(() => setSaveMsg(null), 5000);
    } catch(e) {
      setSaveMsg({ type: "err", text: "Calculation failed" });
    }
    setCalculating(false);
  }, [selectedAsset, selectedBU, selectedPeriod, form, refresh]);

  const handleUpload = useCallback(async (file) => {
    await uploadFile(file, selectedPeriod);
    refresh();
    // Reload current asset data after upload
    if (selectedAsset) {
      client.get(`/dangote/submissions/${selectedAsset.asset_id}/${selectedPeriod}`)
        .then(r => { const ad = r.data?.activity_data||{}; setForm(ad); setSavedForm(ad); })
        .catch(() => {});
      client.get(`/dangote/calculations/${selectedAsset.asset_id}/${selectedPeriod}`)
        .then(r => setCalcResult(r.data?.results||null))
        .catch(() => {});
    }
  }, [uploadFile, selectedPeriod, selectedAsset, refresh]);

  const handleDownloadTemplate = useCallback(() => {
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    const token   = localStorage.getItem("decarboniq_token");
    fetch(`${API_URL}/api/dangote/template/download`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(r => r.blob()).then(blob => {
      const url = URL.createObjectURL(blob);
      const a   = document.createElement("a"); a.href = url;
      a.download = "DangoteMonthlyTemplate.xlsx"; a.click();
      URL.revokeObjectURL(url);
    });
  }, []);

  const handleTransition = useCallback(async (newStatus) => {
    if (!period?.period_id) return;
    setTransitioning(true);
    await transitionPeriod(period.period_id, newStatus);
    setTransitioning(false);
  }, [period, transitionPeriod]);

  const schema      = FIELD_SCHEMA[assetType] || FIELD_SCHEMA.cement;
  const currStatus  = period?.status || "draft";
  const currIdx     = WORKFLOW.indexOf(currStatus);
  const isLocked    = currStatus === "locked";
  const hasUnsaved  = JSON.stringify(form) !== JSON.stringify(savedForm);

  if (loading && !period) return (
    <div className="p-6 lg:p-8 space-y-4">
      {[1,2,3].map(i=><div key={i} className="h-14 bg-slate-100 rounded-xl animate-pulse"/>)}
    </div>
  );

  return (
    <div className="p-6 lg:p-8 space-y-6">
      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-0.5">Dangote Industries</p>
          <PageHeader title="Monthly Data" />
          <p className="text-xs text-slate-400 mt-0.5">
            Input + calculation engine · All downstream pages read from this monthly output
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select value={selectedPeriod||""} onChange={e=>setSelectedPeriod(e.target.value)}
            className="text-xs border border-slate-200 rounded-lg px-2.5 py-1.5 bg-white text-slate-700"
            data-testid="period-selector">
            {periods.map(p=><option key={p.period_id} value={p.period_id}>{p.label} — {p.status}</option>)}
          </select>
          {period && <span className={`text-xs font-bold px-2 py-0.5 rounded-full border ${STATUS_CONFIG[currStatus]?.color}`}>{STATUS_CONFIG[currStatus]?.label}</span>}
          {!isLocked && currIdx < WORKFLOW.length-1 && (
            <button onClick={()=>handleTransition(WORKFLOW[currIdx+1])} disabled={transitioning}
              className="px-3 py-1.5 bg-teal-600 text-white text-xs font-bold rounded-xl disabled:opacity-50 flex items-center gap-1">
              {transitioning&&<RefreshCw size={9} className="animate-spin"/>} → {WORKFLOW[currIdx+1]?.replace(/_/g," ").replace(/\b\w/g,c=>c.toUpperCase())}
            </button>
          )}
          {currStatus==="approved" && (
            <button onClick={()=>handleTransition("locked")} disabled={transitioning}
              className="px-3 py-1.5 bg-slate-700 text-white text-xs font-bold rounded-xl flex items-center gap-1">
              <Lock size={9}/> Lock
            </button>
          )}
        </div>
      </div>

      {/* ── Workflow steps ──────────────────────────────────────────────── */}
      <SectionCard>
        <div className="flex items-center">
          {WORKFLOW.map((step, i) => {
            const past = i < currIdx; const current = i === currIdx;
            return (
              <React.Fragment key={step}>
                <div className="flex flex-col items-center text-center">
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0 ${
                    past?"bg-teal-600 text-white":current?"bg-teal-500 text-white":"bg-slate-100 text-slate-400"
                  }`}>{past?"✓":i+1}</div>
                  <p className={`text-[8px] mt-0.5 uppercase tracking-wide whitespace-nowrap ${
                    current?"text-teal-700 font-bold":past?"text-teal-500":"text-slate-400"
                  }`}>{step.replace(/_/g," ")}</p>
                </div>
                {i < WORKFLOW.length-1 && <div className={`flex-1 h-0.5 mx-1 mb-3.5 ${i<currIdx?"bg-teal-500":"bg-slate-200"}`}/>}
              </React.Fragment>
            );
          })}
        </div>
      </SectionCard>

      {/* ── BU Selector tabs ───────────────────────────────────────────── */}
      <div>
        <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">Select Business Unit</p>
        <div className="flex items-center gap-2 flex-wrap" data-testid="bu-selector">
          {allBUs.map(bu => (
            <button key={bu.bu_id} onClick={() => { setSelectedBU(bu); setSelectedAsset(null); setCalcResult(null); }}
              className={`px-3 py-2 rounded-xl border text-xs font-semibold transition-all ${
                selectedBU?.bu_id === bu.bu_id
                  ? "bg-[#0F172A] text-white border-transparent"
                  : "bg-white border-slate-200 text-slate-600 hover:border-slate-400"
              }`} data-testid={`bu-tab-${bu.sector_type}`}>
              {bu.name.replace("Dangote ", "")}
            </button>
          ))}
        </div>
      </div>

      {selectedBU && (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">

          {/* Left: Input form + Upload */}
          <div className="lg:col-span-3 space-y-4">

            {/* Asset selector */}
            {buAssets.length > 0 && (
              <SectionCard>
                <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">Select Asset</p>
                <div className="flex items-center gap-2 flex-wrap">
                  {buAssets.map(a => (
                    <button key={a.asset_id} onClick={() => { setSelectedAsset(a); setCalcResult(null); }}
                      className={`px-3 py-2 rounded-lg border text-xs font-semibold transition-all ${
                        selectedAsset?.asset_id === a.asset_id
                          ? "bg-teal-600 text-white border-transparent"
                          : "bg-white border-slate-200 text-slate-600 hover:border-teal-400"
                      }`} data-testid={`asset-tab-${a.asset_id}`}>
                      {a.asset_id}
                      {a.has_calc && <span className="ml-1 text-[9px]">✓</span>}
                    </button>
                  ))}
                </div>
              </SectionCard>
            )}

            {/* Dynamic input fields */}
            {selectedAsset && (
              <div className="space-y-3" data-testid={`input-form-${assetType}`}>
                {schema.groups.map(group => (
                  <div key={group.label} className={`border rounded-xl p-4 ${group.color}`}>
                    <p className="text-xs font-bold text-slate-700 uppercase tracking-wide mb-3 flex items-center gap-1.5">
                      <span>{group.icon}</span> {group.label}
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {group.fields.map(field => (
                        <div key={field.key}>
                          <label className="text-xs font-semibold text-slate-700 flex items-center gap-1 mb-1">
                            {field.label}
                            {field.required && <span className="text-red-500">*</span>}
                            <span className="text-slate-400 font-normal">({field.unit})</span>
                          </label>
                          <input
                            type="number"
                            value={form[field.key] || ""}
                            onChange={e => handleFieldChange(field.key, e.target.value)}
                            disabled={isLocked}
                            placeholder={field.placeholder}
                            className="w-full text-xs border border-slate-200 rounded-lg px-3 py-2 bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent disabled:bg-slate-50 disabled:text-slate-400"
                            data-testid={`field-${field.key}`}
                          />
                          {field.hint && <p className="text-[9px] text-slate-400 mt-0.5 italic">{field.hint}</p>}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}

                {/* Action buttons */}
                {!isLocked && (
                  <div className="flex items-center gap-3">
                    <button onClick={handleCalculate} disabled={calculating || saving}
                      className="flex-1 flex items-center justify-center gap-2 py-3 bg-[#0F172A] text-white text-sm font-bold rounded-xl hover:bg-slate-800 disabled:opacity-50 transition-colors"
                      data-testid="calculate-btn">
                      {calculating ? <RefreshCw size={14} className="animate-spin"/> : <Calculator size={14}/>}
                      {calculating ? "Calculating..." : "Calculate Emissions"}
                    </button>
                    <button onClick={handleSaveDraft} disabled={saving || !hasUnsaved}
                      className="px-4 py-3 border border-slate-200 text-slate-600 text-xs font-bold rounded-xl hover:bg-slate-50 disabled:opacity-40"
                      data-testid="save-draft-btn">
                      {saving ? "Saving..." : "Save Draft"}
                    </button>
                  </div>
                )}

                {/* Save message */}
                {saveMsg && (
                  <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold ${
                    saveMsg.type === "ok" ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-red-50 text-red-700 border border-red-200"
                  }`}>
                    {saveMsg.type === "ok" ? <CheckCircle2 size={12}/> : <AlertTriangle size={12}/>}
                    {saveMsg.text}
                  </div>
                )}

                {hasUnsaved && !isLocked && (
                  <p className="text-xs text-amber-600 italic flex items-center gap-1">
                    <AlertTriangle size={10}/> Unsaved changes — click Calculate or Save Draft
                  </p>
                )}
              </div>
            )}

            {/* Upload section */}
            <SectionCard>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="text-xs font-bold text-slate-900 uppercase tracking-wide">Bulk Upload</p>
                  <p className="text-xs text-slate-400">Upload Excel/CSV to populate all assets</p>
                </div>
                <button onClick={handleDownloadTemplate}
                  className="flex items-center gap-1.5 px-3 py-1.5 border border-slate-200 rounded-lg text-xs font-semibold text-slate-600 hover:border-teal-400 hover:text-teal-700"
                  data-testid="download-template">
                  <Download size={12}/> Template
                </button>
              </div>
              <UploadZone onUpload={handleUpload} uploading={importing} />
              {importResult && (
                <div className={`mt-3 rounded-xl p-4 border ${
                  importResult.rows_imported > 0 ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"
                }`} data-testid="import-result">
                  <div className="flex items-center gap-2 mb-2">
                    {importResult.rows_imported > 0
                      ? <CheckCircle2 size={14} className="text-emerald-500"/>
                      : <AlertTriangle size={14} className="text-amber-500"/>}
                    <p className="text-xs font-bold text-slate-900">
                      {importResult.rows_imported}/{importResult.rows_total} rows imported · {importResult.rows_recalculated} calculated
                    </p>
                    <button onClick={() => setImportResult(null)} className="ml-auto text-slate-400 hover:text-slate-700"><X size={12}/></button>
                  </div>
                  {(importResult.errors||[]).length > 0 && (
                    <div className="space-y-0.5">
                      {importResult.errors.slice(0,3).map((e,i)=>(
                        <p key={i} className="text-xs text-amber-600">• {e}</p>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </SectionCard>
          </div>

          {/* Right: Calculation Output ──────────────────────────────────── */}
          <div className="lg:col-span-2">
            {calcResult ? (
              <CalcOutput result={calcResult} assetType={assetType} />
            ) : (
              <div className="bg-slate-50 border border-dashed border-slate-300 rounded-xl p-8 text-center h-full flex flex-col items-center justify-center">
                <Calculator size={24} className="text-slate-300 mb-3" />
                <p className="text-sm font-semibold text-slate-500">Calculation Output</p>
                <p className="text-xs text-slate-400 mt-1">Enter data and click Calculate to see Scope 1/2/3 emissions and circularity metrics here</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Next Actions ─────────────────────────────────────────────────── */}
      <SectionCard>
        <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-2">Downstream Pages (reading from this month's output)</p>
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-2">
          {[
            ["Corporate Dashboard",  "/corporate/dashboard",       true],
            ["Climate & Circularity","/corporate/climate",         true],
            ["Carbon Strategy",      "/corporate/carbon-strategy", true],
            ["Financing",            "/corporate/financing",       true],
            ["Reports",              "/corporate/reports",         true],
          ].map(([l, path, active]) => (
            <button key={path} onClick={() => navigate(path)}
              className="flex items-center justify-between px-3 py-2.5 rounded-xl border bg-white border-slate-200 text-xs font-semibold text-slate-600 hover:border-teal-400 hover:text-teal-700 transition-colors group">
              {l} <ChevronRight size={10} className="text-slate-300 group-hover:text-teal-500" />
            </button>
          ))}
        </div>
      </SectionCard>
    </div>
  );
}
